import { PortableText } from "@portabletext/react";
import { useRouter } from "next/compat/router";
import { GetStaticPaths, GetStaticProps, NextPage } from "next/types";
import { ParsedUrlQuery } from "querystring";
import React from "react";
import { BrandIdentity } from "~/components/BrandIdentity/BrandIdentity";
import { fetchBrandIdentity } from "~/components/BrandIdentity/useBrandIdentity";
import Footer from "~/components/Footer";
import LegalPageLayout from "~/components/LegalPageLayout";
import { portableTextComponents } from "~/components/Sanity/PortableTextComponents";
import { PublicPageSeo } from "~/components/Seo";
import { AVAILABLE_LOCALES } from "~/core/locale";
import { homepageRoute } from "~/core/routes";
import { serverSideTranslations } from "~/translations/serverSideTranslations";
import { ServerSideGeneratedI18nNamespace } from "~/translations/types";
import { LegalPage } from "./_api/LegalPage";
import { LegalPageIds, fetchLegalPageById } from "./_api/useLegalPage";

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface GeneralConditionOfSalePageParams extends ParsedUrlQuery {
  legalPageId: string;
}

interface GeneralConditionOfSalePageProps
  extends ServerSideGeneratedI18nNamespace {
  locale: AVAILABLE_LOCALES;
  legalPage: LegalPage;
  prefetchedBrandIdentity: BrandIdentity;
}

type LegalPagesPaths = Array<{
  params: GeneralConditionOfSalePageParams;
  locale?: string | undefined;
}>;

export const getStaticPaths: GetStaticPaths<
  GeneralConditionOfSalePageParams
> = async () => {
  const paths: LegalPagesPaths = [];

  for (const oneLegalPageId of LegalPageIds) {
    paths.push({
      params: {
        legalPageId: oneLegalPageId,
      },
    });
    for (const locale in AVAILABLE_LOCALES) {
      paths.push({
        params: {
          legalPageId: oneLegalPageId,
        },
        locale: locale,
      });
    }
  }

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps<
  GeneralConditionOfSalePageProps,
  GeneralConditionOfSalePageParams
> = async (context) => {
  const locale = (context.locale ?? context.defaultLocale) as AVAILABLE_LOCALES;
  const legalPageId = context.params?.legalPageId;

  if (!legalPageId) {
    return { notFound: true };
  }

  const legalPage = await fetchLegalPageById({ legalPageId, locale });

  if (legalPage === undefined) {
    return { notFound: true };
  }

  const prefetchedBrandIdentity = await fetchBrandIdentity();

  return {
    props: {
      locale,
      legalPage,
      prefetchedBrandIdentity,
      ...(await serverSideTranslations(locale)),
    },
  };
};

const GeneralConditionOfSalePage: NextPage<GeneralConditionOfSalePageProps> = ({
  locale,
  legalPage,
  prefetchedBrandIdentity,
}: GeneralConditionOfSalePageProps) => {
  const router = useRouter();

  if (!router) {
    return null;
  }

  return (
    <>
      <PublicPageSeo />

      <LegalPageLayout homePageUrl={homepageRoute()}>
        <PortableText
          value={legalPage.text}
          components={portableTextComponents}
        />
      </LegalPageLayout>

      <Footer
        pressKitUrl={prefetchedBrandIdentity.pressKitUrl}
        currentLocale={locale}
        availableLocales={Object.values(AVAILABLE_LOCALES)}
        onLocaleChange={(newLocale) => {
          router
            .push(
              {
                pathname: router.pathname,
                query: router.query,
              },
              undefined,
              {
                locale: newLocale,
              },
            )
            .then(() => {
              window.scrollTo(0, 0);
              router.reload();
            });
        }}
      />
    </>
  );
};

export default GeneralConditionOfSalePage;
